import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import type { components } from "./zz_schema";

import { useAuth } from "@/auth/auth";

export type TunnelRow = components["schemas"]["Tunnel"];
export type TunnelCreate = components["schemas"]["TunnelCreate"];
export type TunnelTokenCreate = components["schemas"]["TunnelTokenCreate"];

const tunnelsKey = (organizationId?: string) => (organizationId ? `/v1/organizations/${organizationId}/tunnels` : null);

type TunnelList = { data?: { tunnels: TunnelRow[] } };

// refreshInterval defaults to following connectors coming and going; a view
// waiting for one to connect passes a shorter one, or one that stops.
export function useOrganizationTunnels(
	organizationId?: string,
	refreshInterval: number | ((latest: TunnelList | undefined) => number) = 15_000,
) {
	const auth = useAuth();

	return useSWR(
		tunnelsKey(organizationId),
		() => {
			if (!organizationId) return undefined;
			return auth.client.GET("/v1/organizations/{organizationId}/tunnels", {
				params: { path: { organizationId } },
			});
		},
		{ refreshInterval },
	);
}

export function useCreateOrganizationTunnel(organizationId?: string) {
	const auth = useAuth();

	return useSWRMutation(tunnelsKey(organizationId), (_, { arg }: { arg: TunnelCreate }) => {
		if (!organizationId) return undefined;
		return auth.client.POST("/v1/organizations/{organizationId}/tunnels", {
			params: { path: { organizationId } },
			body: arg,
		});
	});
}

export function useUpdateOrganizationTunnel(organizationId?: string) {
	const auth = useAuth();

	return useSWRMutation(tunnelsKey(organizationId), (_, { arg }: { arg: { id: string; selectors: string[] } }) => {
		if (!organizationId) return undefined;
		return auth.client.PATCH("/v1/organizations/{organizationId}/tunnels/{id}", {
			params: { path: { organizationId, id: arg.id } },
			body: { selectors: arg.selectors },
		});
	});
}

export function useDeleteOrganizationTunnel(organizationId?: string) {
	const auth = useAuth();

	return useSWRMutation(tunnelsKey(organizationId), (_, { arg }: { arg: { id: string } }) => {
		if (!organizationId) return undefined;
		return auth.client.DELETE("/v1/organizations/{organizationId}/tunnels/{id}", {
			params: { path: { organizationId, id: arg.id } },
		});
	});
}

// useRotateTunnelToken issues a tunnel's token, which revokes the one it had.
export function useRotateTunnelToken(organizationId?: string) {
	const auth = useAuth();

	return useSWRMutation(tunnelsKey(organizationId), (_, { arg }: { arg: { id: string; token: TunnelTokenCreate } }) => {
		if (!organizationId) return undefined;
		return auth.client.POST("/v1/organizations/{organizationId}/tunnels/{id}/token", {
			params: { path: { organizationId, id: arg.id } },
			body: arg.token,
		});
	});
}

// tokenWarningDays is how soon before its expiry a token is flagged, the same
// window in which its connector warns in its own log.
const tokenWarningDays = 14;

export function tokenExpiry(expiresAt: string) {
	const days = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
	return { expired: days < 0, soon: days >= 0 && days < tokenWarningDays, days };
}

